// src/common/verification/resolve-stage.js
import { extractErrorMessage } from '../utils/error-utils.js';
import { updateCitationUI } from '../ui.js';

/**
 * Resolve a citation to a source file.
 * Handles caching and API calls.
 * 
 * @param {Object} citation - The citation object to resolve
 * @param {Object} footnote - The parent footnote
 * @param {Array} parsedCitations - All parsed citations for this footnote
 * @param {Object} options - Configuration options
 * @param {string} options.apiBase - API base URL
 * @param {Function} options.getAuthHeaders - Function to get auth headers
 * @param {boolean} options.isWebApp - Whether running in web app mode
 * @param {Object} options.commonPayload - Common payload for API calls
 * @param {Object} options.cachedResolution - Cached resolution result (optional)
 * @param {boolean} options.skipCache - Whether to skip cache (for "Check Again")
 * @param {boolean} options.forceResolutionRefresh - Bypass the hosted resolver's
 *   durable cache after a failed resolution.
 * @param {HTMLElement} options.resultsContainer - Container element for UI updates (optional)
 * @param {Array<Object>} options.equivalentCitations - Deduplicated citations which
 *   must receive the same persisted resolution before cross-references run
 * @returns {Promise<Object>} Resolution result
 */
export async function resolveCitation(citation, footnote, parsedCitations, options = {}) {
  const {
    apiBase,
    getAuthHeaders,
    isWebApp = false,
    commonPayload = {},
    cachedResolution = null,
    skipCache = false,
    forceResolutionRefresh = false,
    resultsContainer = null,
    equivalentCitations = null,
  } = options;

  // Check for cached resolution (skip if Check Again)
  // A multi-member work group still needs one bridge round-trip: the local DB may
  // contain only the member that originally performed the resolve. The API uses
  // that cheap cache hit to seed every sibling ID before cross-references run.
  const needsAliasBarrier = Array.isArray(equivalentCitations) && equivalentCitations.length > 1;
  if (!skipCache && cachedResolution && !needsAliasBarrier) {
    console.log('[ResolveStage] Using cached resolution for citation', citation.id);
    if (resultsContainer) {
      updateCitationUI(resultsContainer, citation.id, { state: 'resolved', message: 'Resolved source (cached)' });
    }
    // Replay the WHOLE cached resolution, not just its identity fields. A bare
    // file_id is ambiguous: file ids are per-database autoincrements, so a
    // resolution owned by a linked library or a group peer (source_db) points at
    // an unrelated work when replayed against the document's own DB. The rest of
    // the shape (url, pinpoint, agent judgment) is what the analyze stage needs to
    // gather evidence, so a trimmed replay silently degrades congruence too.
    const replayed = {
      cite_id: citation.id,
      file_id: cachedResolution.file_id,
      method: cachedResolution.method,
      score: cachedResolution.score,
      failure_reason: cachedResolution.failure_reason,
      source_db: cachedResolution.source_db,
      url: cachedResolution.url,
      pinpoint: cachedResolution.pinpoint,
      paywalled: cachedResolution.paywalled,
      web_exists: cachedResolution.web_exists,
      failed_urls: cachedResolution.failed_urls,
      agent_hits: cachedResolution.agent_hits,
      agent_judgment_label: cachedResolution.agent_judgment_label,
      agent_judgment_confidence: cachedResolution.agent_judgment_confidence,
      agent_assessment: cachedResolution.agent_assessment,
      document_check_verdict: cachedResolution.document_check_verdict,
      document_check_confidence: cachedResolution.document_check_confidence,
      document_check_rationale: cachedResolution.document_check_rationale,
      frequent_doc_metadata: cachedResolution.frequent_doc_metadata,
    };
    return Object.fromEntries(
      Object.entries(replayed).filter(([, value]) => value !== undefined)
    );
  }

  if (resultsContainer) {
    updateCitationUI(resultsContainer, citation.id, { state: 'resolving', message: 'Resolving source...' });
  }

  // Ensure citation has footnote_id for ask_user routing
  const citationWithFootnoteId = {
    ...citation,
    footnote_id: citation.footnote_id || footnote.id
  };

  console.log(`[ResolveStage] Resolving citation ${citation.id} with footnote_id: ${citationWithFootnoteId.footnote_id}`);

  const endpoint = isWebApp ? '/web/audit/resolve' : '/audit/resolve';
  const requestBody = JSON.stringify({
    citation: citationWithFootnoteId,
    equivalent_citations: Array.isArray(equivalentCitations) ? equivalentCitations : null,
    footnote,
    parsed_citations: parsedCitations,
    ...commonPayload,
    ...(isWebApp && forceResolutionRefresh ? { force_resolution_refresh: true } : {})
  });

  const sendResolveRequest = async (authOptions = {}) => {
    const authHeaders = await getAuthHeaders(authOptions);
    return fetch(`${apiBase}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: requestBody,
    });
  };

  let res = await sendResolveRequest();
  if (res.status === 401) {
    console.warn(`[ResolveStage] 401 for ${citation.id}; refreshing token and retrying once`);
    res = await sendResolveRequest({ forceRefresh: true });
  }

  const data = await res.json();
  console.log(`[ResolveStage] Resolve response for ${citation.id}:`, data);

  if (!res.ok || data.status === 'error') {
    const errorMsg = data.error || extractErrorMessage(data) || `Failed to resolve ${citation.id}`;
    console.error(`[ResolveStage] Resolve error for ${citation.id}:`, errorMsg);
    throw new Error(errorMsg);
  }

  const resolveResult = data.result;
  if (!resolveResult) {
    console.error(`[ResolveStage] No result in resolveData for ${citation.id}`);
    throw new Error('No result returned from resolve endpoint');
  }

  if (resultsContainer) {
    updateCitationUI(resultsContainer, citation.id, { state: 'resolved', message: 'Resolved source' });
  }

  return resolveResult;
}
